/* eslint-disable max-lines */
import { AttendanceVerificationMethod } from "@clipboard-health/contract-backend-main";
import { type WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { isDefined, isNil } from "@clipboard-health/util-ts";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { type ScoreFreezeReason } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { type FacilityRule } from "@src/appV2/Facilities/types";
import { useToast } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useScreenBrightness } from "@src/appV2/lib/utils";
import { useTimeoutTimer } from "@src/appV2/lib/utils/useTimeoutTimer";
import { BreakConfirmationBottomSheet } from "@src/appV2/redesign/Shift/BreakConfirmationBottomSheet/BreakConfirmationBottomSheet";
import {
  BottomSheet,
  BottomSheetLoadingContent,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { getSafeAreaPaddings } from "@src/appV2/redesign/system/ui-theme";
import { useSkippableWait } from "@src/appV2/redesign/utils/useSkippableWait";
import {
  getWorkerShiftPath,
  type GetWorkerShiftResponse,
} from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShift";
import {
  ShiftMarkedNonIpReasons,
  useMarkShiftNonInstant,
} from "@src/appV2/redesign/WorkerShifts/api/useMarkShiftNonInstant";
import {
  ActionCheckType,
  ShiftStage,
  useRecordShiftTimekeepingAction,
} from "@src/appV2/redesign/WorkerShifts/api/useRecordShiftTimekeepingAction";
import { MY_SHIFTS_BACKGROUND_CHECK_ERROR_FULL_PATH } from "@src/appV2/redesign/WorkerShifts/path";
import { NfcHashValidationAction } from "@src/appV2/redesign/WorkerShifts/Timekeeping/NfcValidation/api/types";
import { WorkerNfcValidation } from "@src/appV2/redesign/WorkerShifts/Timekeeping/NfcValidation/WorkerNfcValidation";
import { AcceptNonInstantPayContent } from "@src/appV2/redesign/WorkerShifts/Timekeeping/TimekeepingActions/AcceptNonInstantPayContent";
import { TimekeepingActionSuccessContent } from "@src/appV2/redesign/WorkerShifts/Timekeeping/TimekeepingActions/TimekeepingActionSuccessContent";
import { getClockInSuccessDescription } from "@src/appV2/redesign/WorkerShifts/Timekeeping/TimekeepingActions/utils/getClockInSuccessDescription";
import { getHasWorkerClockedInOnTime } from "@src/appV2/redesign/WorkerShifts/Timekeeping/TimekeepingActions/utils/getHasWorkerClockedInOnTime";
import { isBackgroundCheckError } from "@src/appV2/redesign/WorkerShifts/Timekeeping/TimekeepingActions/utils/handleBackgroundCheckError";
import { WorkplaceRulesReminderContent } from "@src/appV2/redesign/WorkerShifts/Timekeeping/TimekeepingActions/WorkplaceRulesReminderContent";
import { NoteAcknowledgementAction } from "@src/appV2/Shifts/MandatoryBreakPolicy/types";
import { useGetShiftMutation } from "@src/appV2/Shifts/Shift/api/useGetShiftMutation";
import { type TouchpointCodeGeneratorProps } from "@src/appV2/Shifts/Shift/TouchpointIntegration/touchpointCodeGenerator";
import { useTouchpointFirstViewTime } from "@src/appV2/Shifts/Shift/TouchpointIntegration/useTouchpointFirstViewTime";
import {
  type NfcTagRequest,
  type TimeRange,
  type WorkerShift,
} from "@src/appV2/Shifts/Shift/types";
import { useQueryClient } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { QrCodeDisplay } from "../Stages/QrCodeDisplay";
import { ScanInstructions } from "../Stages/ScanInstructions";
import { TouchpointSkipWarning } from "../Stages/TouchpointSkipWarning";

export interface ClockInTouchpointBottomSheetProps {
  modalState: UseModalState;
  workerId: string;
  workplaceId: string;
  workerStage: WorkerStage;
  workerSubstage?: string;
  workplaceName: string;
  kioskLocation: string;
  qrCodeProps: TouchpointCodeGeneratorProps;
  shift: WorkerShift;
  isNfcEnabled: boolean;
  nfcTagRequests: NfcTagRequest[];
  timeToAllowSkipInSec: number;
  requiresBreakPolicyAcknowledgement: boolean;
  breakPolicyNoteId: string;
  workplaceRules?: FacilityRule[];
  shouldShowWorkplaceRulesReminder: boolean;
  isSecondShiftInDouble: boolean;
  isClipboardScoreFreezeEnabled: boolean;
  clipboardScoreFreezeReason?: ScoreFreezeReason;
  targetArrivalTime?: Date;
  shiftStartDate?: Date;
  shiftBookedAt?: Date;
  isTimesheetEditDisabled: boolean;
  shouldEnforceNfcTimesheetBounds: boolean;
  isArrivalTimeFeatureEnabled: boolean;
  isMissedPunchRequestEnabled: boolean;
  timezone?: string;
  onTimekeepingActionCompleted: (props?: { nonInstantPayReason?: string }) => void;
  onTimekeepingActionFailed: (props: { error: string }) => void;
  onClockInFlowCompleted: () => void;
}

type ClockInTouchpointStep =
  | "instructions"
  | "qr-display"
  | "clock-in-success"
  | "skip-warning"
  | "accept-non-instant-pay"
  | "requesting-non-instant-pay"
  | "touchpoint-nfc-check"
  | "workplace-rules";

const REFETCH_SHIFT_INTERVAL_IN_MS = 3000;
const SUCCESS_MESSAGE_DURATION_MS = 3000;

export function ClockInTouchpointBottomSheet(props: ClockInTouchpointBottomSheetProps) {
  const {
    modalState,
    workerId,
    workplaceId,
    workerStage,
    workerSubstage,
    workplaceName,
    kioskLocation,
    qrCodeProps,
    shift,
    isNfcEnabled,
    nfcTagRequests,
    timeToAllowSkipInSec,
    requiresBreakPolicyAcknowledgement: requiresBreakPolicyAcknowledgementProperty,
    breakPolicyNoteId,
    workplaceRules,
    shouldShowWorkplaceRulesReminder,
    isSecondShiftInDouble,
    targetArrivalTime,
    shiftStartDate,
    timezone,
    shiftBookedAt,
    isTimesheetEditDisabled,
    shouldEnforceNfcTimesheetBounds,
    isArrivalTimeFeatureEnabled,
    isClipboardScoreFreezeEnabled,
    clipboardScoreFreezeReason,
    isMissedPunchRequestEnabled,
    onTimekeepingActionCompleted,
    onTimekeepingActionFailed,
    onClockInFlowCompleted,
  } = props;

  const shiftId = shift._id;

  const history = useHistory();
  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();

  const { data: clipboardScoreProfile } = useGetClipboardScoreProfile(workerId);

  const initialStep: ClockInTouchpointStep = "instructions";

  const [step, setStep] = useState<ClockInTouchpointStep>(initialStep);

  const [requiresBreakPolicyAcknowledgement, setRequiresBreakPolicyAcknowledgement] = useState(
    requiresBreakPolicyAcknowledgementProperty
  );

  const { wait: waitForSuccess, skip: skipSuccessWait } = useSkippableWait();

  const shiftDetailsPollingInterval = useRef<NodeJS.Timeout | undefined>(undefined);

  const firstViewTime = useTouchpointFirstViewTime({
    enabled: modalState.modalIsOpen && step === "qr-display",
    shiftId,
    clockAction: "clock-in",
  });

  useLogEffect(
    APP_V2_USER_EVENTS.CLOCK_IN_MODAL_OPENED,
    {
      shiftId,
      workerId,
      workplaceId,
      clockInType: "touchpoint",
    },
    { enabled: modalState.modalIsOpen }
  );

  // Computes seconds left until the skip button is enabled
  const { secondsLeft: secondsLeftForNotWorking } = useTimeoutTimer({
    initialEndTime: firstViewTime ? firstViewTime + timeToAllowSkipInSec * 1000 : Date.now(),
  });

  const { mutateAsync: markNonInstant } = useMarkShiftNonInstant();
  const { mutateAsync: recordTimekeepingAction, data: postClockInShift } =
    useRecordShiftTimekeepingAction();

  const closeBottomSheet = useCallback(
    (shiftClockInOut?: TimeRange) => {
      logEvent(APP_V2_USER_EVENTS.CLOCK_IN_MODAL_CLOSED, {
        shiftId,
        workerId,
        workplaceId,
        clockInType: "touchpoint",
        step,
      });
      skipSuccessWait();
      modalState.closeModal();

      if (isDefined(shiftClockInOut)) {
        queryClient.setQueryData<GetWorkerShiftResponse>([getWorkerShiftPath(shiftId)], (result) =>
          result
            ? {
                ...result,
                response: {
                  ...result.response,
                  clockInOut: shiftClockInOut,
                },
              }
            : undefined
        );

        onClockInFlowCompleted();
      }
    },
    [
      modalState,
      queryClient,
      shiftId,
      onClockInFlowCompleted,
      skipSuccessWait,
      step,
      workerId,
      workplaceId,
    ]
  );

  const completeSuccessFlow = useCallback(
    async (shiftClockInOut?: TimeRange) => {
      const shiftStartDate = parseISO(shift.start);
      const clockInTimeFromClockInOut = shiftClockInOut?.start
        ? parseISO(shiftClockInOut.start)
        : undefined;

      const wasOnTime = getHasWorkerClockedInOnTime({
        shiftStartDate,
        clockInTime: clockInTimeFromClockInOut,
      });

      logEvent(APP_V2_USER_EVENTS.CLOCK_IN_SUCCESS, {
        shiftId,
        workerId,
        workplaceId,
        clockInType: "touchpoint",
        onTime: wasOnTime,
        clockInTime: clockInTimeFromClockInOut,
        shiftStartDate: shiftStartDate.toISOString(),
      });
      setStep("clock-in-success");
      await waitForSuccess(SUCCESS_MESSAGE_DURATION_MS);
      closeBottomSheet(shiftClockInOut);
    },
    [shiftId, workerId, workplaceId, shift.start, waitForSuccess, closeBottomSheet]
  );

  function clearShiftDetailsPollingInterval() {
    clearInterval(shiftDetailsPollingInterval.current);
    shiftDetailsPollingInterval.current = undefined;
  }

  // Clear the polling interval when the modal is closed or the component is unmounted
  useEffect(() => {
    if (!modalState.modalIsOpen) {
      setStep(initialStep);

      clearShiftDetailsPollingInterval();
    }

    return () => {
      clearShiftDetailsPollingInterval();
    };
  }, [modalState.modalIsOpen, setStep, initialStep]);

  const onClockInSuccess = useCallback(
    async (shiftClockInOut?: TimeRange) => {
      onTimekeepingActionCompleted();

      if (shouldShowWorkplaceRulesReminder && workplaceRules && workplaceRules.length > 0) {
        setStep("workplace-rules");
      } else {
        await completeSuccessFlow(shiftClockInOut);
      }
    },
    [
      completeSuccessFlow,
      shouldShowWorkplaceRulesReminder,
      workplaceRules,
      onTimekeepingActionCompleted,
    ]
  );

  const { mutateAsync: refetchShiftDetails, data: postAutoClockInShift } = useGetShiftMutation(
    shiftId,
    {
      onSuccess: async (shiftDetails) => {
        if (!modalState.modalIsOpen) {
          return;
        }

        // Check if shift has been clocked in via TouchPoint scan
        if (isDefined(shiftDetails?.response?.clockInOut?.start)) {
          logEvent(APP_V2_USER_EVENTS.TOUCHPOINT_SCAN_SUCCESS, {
            shiftId,
            facilityId: workplaceId,
            workerId,
            action: "clock-in",
          });

          await onClockInSuccess(shiftDetails.response.clockInOut);
        }
      },
    }
  );

  const clockInOutObject =
    postAutoClockInShift?.response?.clockInOut ?? postClockInShift?.clockInOut ?? undefined;

  const clockInTime = clockInOutObject?.start ? parseISO(clockInOutObject.start) : undefined;

  const hasWorkerClockedInOnTime = getHasWorkerClockedInOnTime({
    shiftStartDate: parseISO(shift.start),
    clockInTime,
  });

  // Maximize brightness during QR display
  useScreenBrightness(
    1, // Set brightness to maximum (100%)
    modalState.modalIsOpen && step === "qr-display",
    (error, operation) => {
      if (operation === "set") {
        logError(APP_V2_APP_EVENTS.TOUCHPOINT_QR_SET_MAX_BRIGHTNESS_FAILURE, {
          error,
        });
      } else if (operation === "restore") {
        logError(APP_V2_APP_EVENTS.TOUCHPOINT_QR_RESTORE_BRIGHTNESS_FAILURE, {
          error,
        });
      }
    }
  );

  async function recordClockIn(options?: {
    shiftActionCheck: ActionCheckType;
    verificationMethod: AttendanceVerificationMethod;
  }) {
    try {
      const postMutationShift = await recordTimekeepingAction({
        shiftId,
        stage: ShiftStage.CLOCK_IN,
        ...options,
      });

      await onClockInSuccess(postMutationShift.clockInOut ?? undefined);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to clock in, please try again.";

      onTimekeepingActionFailed({ error: errorMessage });

      if (isBackgroundCheckError(error)) {
        modalState.closeModal();
        history.replace(MY_SHIFTS_BACKGROUND_CHECK_ERROR_FULL_PATH);
        return;
      }

      showErrorToast(errorMessage);
    }
  }

  async function convertToNonInstantPay() {
    try {
      await markNonInstant({
        shiftId,
        reason: ShiftMarkedNonIpReasons.TOUCHPOINT_VALIDATION_FAILURE,
      });

      onTimekeepingActionCompleted({
        nonInstantPayReason: "TOUCHPOINT_VALIDATION_FAILURE",
      });

      modalState.closeModal();
    } catch (error: unknown) {
      logEvent(APP_V2_USER_EVENTS.TOUCHPOINT_CONVERSION_TO_NON_INSTANT_FAILED, {
        shiftId,
        facilityId: workplaceId,
        workerId,
        action: "clock-in",
        error: error instanceof Error ? error.message : "Unknown error",
      });

      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to convert to non-instant pay, please try again.";
      showErrorToast(errorMessage);

      closeBottomSheet();
    }
  }

  if (requiresBreakPolicyAcknowledgement) {
    return (
      <BreakConfirmationBottomSheet
        action={NoteAcknowledgementAction.CLOCK_IN}
        isLoading={false}
        modalState={modalState}
        shiftId={shiftId}
        workplaceId={workplaceId}
        breakPolicyNoteId={breakPolicyNoteId}
        breakPolicy={shift.breakPolicy}
        onAcknowledge={async () => {
          setRequiresBreakPolicyAcknowledgement(false);
        }}
      />
    );
  }

  return (
    <BottomSheet
      modalState={modalState}
      canDismiss={false}
      paperSx={getSafeAreaPaddings({ bottom: true })}
    >
      {step === "touchpoint-nfc-check" && (
        <WorkerNfcValidation
          shiftId={shiftId}
          metaProps={{
            workerId,
            workplaceId,
            workplaceName,
          }}
          nfcTagRequests={nfcTagRequests}
          clockAction={NfcHashValidationAction.CLOCK_IN}
          isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
          onSuccess={() => {
            void recordClockIn({
              shiftActionCheck: ActionCheckType.NFC,
              verificationMethod: AttendanceVerificationMethod.NFC_TAG,
            });
          }}
          onSkipLocationCheck={async () => {
            setStep("requesting-non-instant-pay");
            await convertToNonInstantPay();
          }}
          onCancel={closeBottomSheet}
          onNfcNotSupported={() => {
            setStep("accept-non-instant-pay");
          }}
        />
      )}

      {step === "instructions" && (
        <ScanInstructions
          kioskLocation={kioskLocation}
          isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
          onContinue={() => {
            setStep("qr-display");
          }}
          onKioskNotAvailable={() => {
            logEvent(APP_V2_USER_EVENTS.ALREADY_STARTED_WORKING_BUTTON_TAPPED, {
              button: "mesh-kiosk-not-available",
              shiftId,
              facilityId: workplaceId,
              workerId,
            });
            setStep("accept-non-instant-pay");
          }}
          onCancel={closeBottomSheet}
        />
      )}

      {step === "qr-display" && (
        <QrCodeDisplay
          qrCodeProps={{
            ...qrCodeProps,
            buttonTapTime: firstViewTime ? new Date(firstViewTime) : undefined,
          }}
          kioskLocation={kioskLocation}
          secondsLeft={secondsLeftForNotWorking}
          isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
          onQrCodeReady={() => {
            if (isNil(shiftDetailsPollingInterval.current)) {
              // Start polling for shift changes using the same pattern as legacy TouchPoint
              shiftDetailsPollingInterval.current = setInterval(() => {
                void refetchShiftDetails(undefined);
              }, REFETCH_SHIFT_INTERVAL_IN_MS);
            }
          }}
          onSkip={() => {
            logEvent(APP_V2_USER_EVENTS.TOUCHPOINT_SCAN_FAILURE, {
              shiftId,
              facilityId: workplaceId,
              workerId,
              action: "clock-in",
            });

            setStep("skip-warning");
          }}
          onQrScanNotWorking={() => {
            logEvent(APP_V2_USER_EVENTS.ALREADY_STARTED_WORKING_BUTTON_TAPPED, {
              button: "mesh-qr-scan-not-working",
              shiftId,
              facilityId: workplaceId,
              workerId,
            });
            setStep("accept-non-instant-pay");
          }}
          onCancel={() => {
            setStep("instructions");
          }}
        />
      )}

      {step === "clock-in-success" && (
        <TimekeepingActionSuccessContent
          title="Have a great shift!"
          description={getClockInSuccessDescription({
            hasWorkerClockedInOnTime,
            isSecondShiftInDouble,
            earliestGeofenceEntryTime: isDefined(shift.geofence?.entry)
              ? parseISO(shift.geofence.entry)
              : undefined,
            isTimesheetEditDisabled,
            shouldEnforceNfcTimesheetBounds,
            isArrivalTimeFeatureEnabled,
            targetArrivalTime,
            shiftStartDate,
            clockInTime,
            timezone,
            shiftBookedAt,
            policy: clipboardScoreProfile?.policy,
            workerStage,
            workerSubstage,
            isClipboardScoreFreezeEnabled,
            clipboardScoreFreezeReason,
          })}
          onClose={closeBottomSheet}
        />
      )}

      {step === "accept-non-instant-pay" && (
        <AcceptNonInstantPayContent
          isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
          onAccept={async () => {
            setStep("requesting-non-instant-pay");
            await convertToNonInstantPay();
          }}
          onCancel={() => {
            setStep(initialStep);
          }}
        />
      )}

      {step === "requesting-non-instant-pay" && (
        <BottomSheetLoadingContent
          title="Converting to non-instant pay"
          description="Should only take a few seconds"
        />
      )}

      {step === "skip-warning" && (
        <TouchpointSkipWarning
          facilityName={workplaceName}
          clockAction="clock-in"
          onCancel={() => {
            setStep("qr-display");
          }}
          onContinue={() => {
            if (isNfcEnabled) {
              setStep("touchpoint-nfc-check");
            } else {
              void recordClockIn({
                shiftActionCheck: ActionCheckType.LOCATION,
                verificationMethod: AttendanceVerificationMethod.GEOFENCE,
              });
            }
          }}
        />
      )}

      {step === "workplace-rules" && workplaceRules && (
        <WorkplaceRulesReminderContent
          workplaceRules={workplaceRules}
          shiftId={shiftId}
          workplaceId={workplaceId}
          onContinue={async () => {
            await completeSuccessFlow(clockInOutObject);
          }}
        />
      )}
    </BottomSheet>
  );
}
/* eslint-enable max-lines */
